import React from "react";

/**
 * Returns a sort icon based on the current sort field and direction
 * @param field - The field being checked
 * @param currentSortField - The currently active sort field (can be null)
 * @param sortDirection - The current sort direction ("asc" | "desc")
 * @returns React node with the appropriate sort icon
 */
export function getSortIcon<T extends string>(
  field: T,
  currentSortField: T | null,
  sortDirection: "asc" | "desc",
): React.ReactNode {
  if (currentSortField !== field) {
    return (
      <span style={{ fontSize: "0.7rem", opacity: 0.5, marginLeft: "0.25rem" }}>
        ▼
      </span>
    );
  }
  return (
    <span style={{ fontSize: "0.7rem", marginLeft: "0.25rem" }}>
      {sortDirection === "asc" ? "▲" : "▼"}
    </span>
  );
}

/**
 * Message fragment for loading state
 */
export function LoadingMessage({
  message = "Loading...",
}: {
  message?: string;
}): JSX.Element {
  return <div className="message loading">{message}</div>;
}

/**
 * Message fragment for error state
 */
export function ErrorMessage({ error }: { error: string }): JSX.Element {
  return <div className="message error">Error: {error}</div>;
}

/**
 * Message fragment for empty state
 */
export function EmptyMessage({ message }: { message: string }): JSX.Element {
  return <div className="message empty">{message}</div>;
}

/**
 * Message fragment for warning state
 */
export function WarningMessage({ message }: { message: string }): JSX.Element {
  return <div className="message warning">{message}</div>;
}

/**
 * Status indicator showing connection status
 */
export function StatusIndicator({
  isConnected,
  connectedText = "● LIVE",
  disconnectedText = "○ OFFLINE",
}: {
  isConnected: boolean;
  connectedText?: string;
  disconnectedText?: string;
}): JSX.Element {
  return (
    <div className="status-indicator">
      <span
        className={`status-dot ${isConnected ? "connected" : "disconnected"}`}
      >
        {isConnected ? connectedText : disconnectedText}
      </span>
    </div>
  );
}

/**
 * Layout header fragment
 */
export function LayoutHeader({
  title,
  children,
}: {
  title: React.ReactNode;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="layout-header">
      <div className="layout-header-title">{title}</div>
      {children && <div className="layout-header-actions">{children}</div>}
    </div>
  );
}

/**
 * Table header cell with sorting functionality
 */
export function TableHeaderCell<T extends string>({
  field,
  currentSortField,
  sortDirection,
  onSort,
  children,
  center = false,
}: {
  field: T;
  currentSortField: T | null;
  sortDirection: "asc" | "desc";
  onSort: (field: T) => void;
  children: React.ReactNode;
  center?: boolean;
}): JSX.Element {
  return (
    <div
      className={`table-header-cell ${center ? "center" : ""}`}
      onClick={() => onSort(field)}
    >
      {children}
      {getSortIcon(field, currentSortField, sortDirection)}
    </div>
  );
}

/**
 * Table header cell without sorting (for static headers)
 */
export function TableHeaderCellStatic({
  children,
  center = false,
}: {
  children: React.ReactNode;
  center?: boolean;
}): JSX.Element {
  return (
    <div className={`table-header-cell ${center ? "center" : ""}`}>
      {children}
    </div>
  );
}

/**
 * Cancel button fragment
 */
export function CancelButton({
  onClick,
  disabled,
}: {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}): JSX.Element {
  console.log("[CancelButton] Rendering", {
    disabled,
    hasOnClick: !!onClick,
  });

  return (
    <button
      onClick={(e) => {
        console.log("[CancelButton] Button clicked", {
          disabled,
          event: e,
        });
        if (!disabled && onClick) {
          onClick(e);
        } else {
          console.warn("[CancelButton] Click ignored - button is disabled", {
            disabled,
          });
        }
      }}
      onMouseDown={(e) => {
        console.log("[CancelButton] Mouse down", {
          disabled,
          event: e,
        });
      }}
      className="btn btn-danger"
      disabled={disabled}
      style={{
        pointerEvents: disabled ? "none" : "auto",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      Cancel
    </button>
  );
}

/**
 * Close button fragment
 */
export function CloseButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button onClick={onClick} className="btn btn-success">
      Close
    </button>
  );
}

/**
 * Action button fragment with customizable variant
 */
export function ActionButton({
  onClick,
  variant = "danger",
  children,
}: {
  onClick: () => void;
  variant?: "danger" | "success";
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button onClick={onClick} className={`btn btn-${variant}`}>
      {children}
    </button>
  );
}

